import { useEffect, useRef, useState } from "react";
import Select from "react-select";
import { AiFillFileAdd } from "react-icons/ai";
import LocalStorageService from "../Auth/auth_service/AuthServiceStorage";
import AxiosService from "../Utils/AxiosService";
import * as Tooltip from "@radix-ui/react-tooltip";
import JsonToTable from "./JsonToTable";

/* -----------------------------------------------------------------------
   BOOTSTRAP TOOLTIP OPTION COMPONENT  (shared pattern with SingleSelect)
------------------------------------------------------------------------ */
const apiService = new AxiosService();
const storageService = LocalStorageService.getService();

export const TooltipOption = ({ data, cvType, context, defaultLanguage }) => {
  const [info, setInfo] = useState(null);

  const fetchInfo = async () => {
    if (!info) {
      try {
        const response = await apiService.completeCVDetail(cvType, data.value);
        setInfo(response.data);
      } catch (error) {
        console.error("Error fetching CV detail:", error);
      }
    }
  };

  const getLabel = () => {
    if (typeof data.label === "string") return data.label;
    if (defaultLanguage?.name && data.label[defaultLanguage.name]) {
      return data.label[defaultLanguage.name];
    }
    return data.label?.ENGLISH || "";
  };

  return (
    <Tooltip.Provider delayDuration={200}>
      <Tooltip.Root>
        <Tooltip.Trigger asChild>
          <div
            onMouseEnter={fetchInfo}
            className="cursor-pointer text-sm text-gray-800 hover:text-blue-600"
          >
            {context === "value" ? (
              <div>{getLabel()}</div>
            ) : typeof data.label === "string" ? (
              <div>{data.label}</div>
            ) : (
              <div>
                {Object.entries(data.label).map(([lang, value]) => (
                  <div key={lang}>
                    <b className="text-xs text-indigo-900">{lang}:</b> {value}
                  </div>
                ))}
              </div>
            )}
          </div>
        </Tooltip.Trigger>

        {info && (
          <Tooltip.Portal>
            <Tooltip.Content
              side="top"
              sideOffset={8}
              avoidCollisions={true}
              align="start"
              alignOffset={5}
              className="bg-light text-white border border-gray-300 rounded-lg shadow-lg p-2 text-xs max-w-4xl z-100 overflow-y-auto whitespace-normal break-words"
            >
              <JsonToTable data={[info]} defLang={defaultLanguage?.code || ""} />
              <Tooltip.Arrow className="fill-gray-100" />
            </Tooltip.Content>
          </Tooltip.Portal>
        )}
      </Tooltip.Root>
    </Tooltip.Provider>
  );
};

export const CreateFormatOptionLabel = (cvType, defaultLanguage) => {
  return (data, { context }) => (
    <TooltipOption
      data={data}
      cvType={cvType}
      context={context}
      defaultLanguage={defaultLanguage}
    />
  );
};

/* -----------------------------------------------------------------------
                           MAIN COMPONENT
------------------------------------------------------------------------ */

const MultiSelect = ({
  fieldId,
  fieldName,
  fieldType,
  cvType,
  language,
  handleMultiSelectFieldChange,
  fieldValue,       // array of IDs that are currently selected
  isFormReset,
  isSaveAccess,
  enableEdit,
}) => {
  const [optionData, setOptionData] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [, setModal] = useState({ show: false });
  const hasRun = useRef(false);
  const prevFieldValue = useRef(null);

  const [defaultLanguage, setDefaultLanguage] = useState(null);

  useEffect(() => {
    setDefaultLanguage(storageService.getDefLanguage());
  }, []);

  // Sync selected options when fieldValue or language changes
  useEffect(() => {
    if (!defaultLanguage) return;

    const isEmpty =
      !fieldValue || (Array.isArray(fieldValue) && fieldValue.length === 0);

    if (isEmpty) {
      setSelectedOptions([]);
      prevFieldValue.current = null;
      return;
    }

    const valueKey = JSON.stringify(fieldValue);
    if (valueKey !== prevFieldValue.current && language) {
      searchCV(cvType, fieldValue, defaultLanguage);
      prevFieldValue.current = valueKey;
    }
  }, [fieldValue, defaultLanguage, language]);

  // Initial load of selected options
  useEffect(() => {
    if (!defaultLanguage) return;

    const isEmpty =
      !fieldValue || (Array.isArray(fieldValue) && fieldValue.length === 0);

    if (!hasRun.current && !isEmpty) {
      searchCV(cvType, fieldValue, defaultLanguage);
      hasRun.current = true;
    }

    if (isEmpty) {
      setSelectedOptions([]);
      hasRun.current = false;
    }
  }, [fieldValue, defaultLanguage]);

  // Fetch label info for each selected ID from the API
  const searchCV = (cvType, values, language) => {
    const ids = Array.isArray(values) ? values : [values];
    apiService
      .searchCVByKuId(cvType, ids, language)
      .then((resp) => {
        const opts = Object.entries(resp.data).map(([key, value]) => ({
          value: key,
          label: value,
        }));
        setOptionData(opts);
        setSelectedOptions(opts);
      })
      .catch((err) => console.log(err));
  };

  // Reset when parent signals form reset
  useEffect(() => {
    if (isFormReset) {
      setSelectedOptions([]);
      hasRun.current = false;
    }
  }, [isFormReset]);

  // Search dropdown options by label as user types
  const fetchOptions = (inputValue) => {
    const shouldFetch =
      (cvType === "unit_cv" && inputValue.length >= 1) ||
      (cvType !== "unit_cv" && inputValue.length >= 3);

    if (!shouldFetch) return;

    apiService
      .searchInCVByLabelname(inputValue, cvType, "ES")
      .then((resp) => {
        const opts = Object.entries(resp.data).map(([key, value]) => ({
          value: key,
          label: value,
        }));
        setOptionData(opts);
      })
      .catch(console.log);
  };

  const getFlattenedOptions = (opts) =>
    opts.map((o) => ({
      value: o.value,
      label:
        defaultLanguage?.name && o.label[defaultLanguage.name]
          ? o.label[defaultLanguage.name]
          : o.label.ENGLISH,
    }));

  const handleChange = (selected) => {
    const next = selected || [];
    setSelectedOptions(next);
    const flat = getFlattenedOptions(next);
    handleMultiSelectFieldChange(cvType, fieldId, flat);
  };

  return (
    <div className="mb-3">
      <div className={isSaveAccess ? "d-flex gap-2" : ""}>
        <div className="flex-grow-1">
          <Select
            isMulti
            isDisabled={!enableEdit}
            id={fieldId || fieldName}
            name={fieldName}
            className="w-100"
            options={optionData}
            value={selectedOptions}
            onChange={handleChange}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 0 }),
            }}
            getOptionLabel={(opt) =>
              opt.label[defaultLanguage] || Object.values(opt.label)[0]
            }
            getOptionValue={(opt) => opt.value}
            onInputChange={fetchOptions}
            placeholder={`Search ${fieldType}...`}
            formatOptionLabel={CreateFormatOptionLabel(cvType, defaultLanguage)}
            isClearable
          />
        </div>

        {isSaveAccess && (
          <button
            className="btn btn-outline-primary"
            title="Add CV"
            onClick={() => setModal({ show: true })}
          >
            <AiFillFileAdd size={22} />
          </button>
        )}
      </div>
    </div>
  );
};

export default MultiSelect;
